/**
 * Pane 3 — the wire ticket: the operational status of the outgoing wire.
 *
 * Six fields bound to `loanTerms.wireInstructions` (wireRequestDate,
 * wireAmount, wireStatus, wireFedRefNumber, closingWireReleaseDate, wireNotes).
 * This is deliberately NOT the wire math (that is the tie-out pane) and NOT the
 * bank routing/account details (those are policy-gated to last-4 display in the
 * Funding Shield order sidebar off the Orders tab).
 *
 * Backend note carried over from WIRE_AND_FUNDING_TAB.md: the typed
 * `WireInstructions` model exists (`app/models/closing.py:459-473`) but is not
 * yet a field on `LoanTerms`, so the form persists through the GENERIC loan-terms
 * object endpoints (`create_object` / `update_object` on `wireInstructions`).
 * That is why there is no dedicated wire route to call.
 *
 * The two things this pane adds around that form:
 *   • a RELEASE CHECK — the ticket amount against the calculator's
 *     `netWireToTitle`, and the two gates from the reconciliation bar, stated
 *     in the pane where the money actually leaves;
 *   • the ticket's own state read back from the saved fields.
 */
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import LoanFeesSubTabWireInstructionsRedesign from "../../LoanDrilldownFeesTab/tabs/LoanFeesSubTabWireInstructionsRedesign";
import { formatDate } from "../../../../../_src_shared/utils/functions/formatDate";
import {
  Callout,
  EMPTY,
  Pill,
  SectionHead,
  T,
  money,
  signedMoney,
} from "../wireFundingShared";
import {
  hudTieOut,
  lenderChargesTieOut,
  wireAgreement,
} from "../WireReconciliationBar";
import { ticketProgress } from "../FundingWorkRail";
import { PaneHeader, PaneShell, PaneStrip, StripCell } from "./PaneShell";

/** Everything that must be true before the wire is released, as a checklist. */
const releaseChecks = ({ statement, netWire, wireInstructions }) => {
  const agreement = wireAgreement({ statement, netWire, wireInstructions });
  const tieOut = hudTieOut(netWire);
  const lender = lenderChargesTieOut(netWire);
  const status = statement?.statementStatus;

  return [
    {
      key: "locked",
      label: "Funding statement locked",
      ok: status === "Locked" || status === "Acknowledged",
      detail:
        status === "Acknowledged"
          ? "Locked and acknowledged."
          : status === "Locked"
            ? "Snapshot frozen and filed to the Closing Package."
            : "Still a projection — the figures can still move.",
    },
    {
      key: "balance",
      label: "HUD tie-out balances",
      ok: tieOut.state === "balanced",
      detail:
        tieOut.state === "balanced"
          ? "Sources and uses tie out exactly."
          : tieOut.state === "off"
            ? `Off by ${money(tieOut.variance)}.`
            : tieOut.noHud
              ? "No HUD to compare — the title side is prepared from loan fields until a settlement statement is extracted."
              : "No HUD sources or uses on file to tie out against.",
    },
    {
      key: "lenderCharges",
      label: "Lender charges tie out",
      ok: lender.state === "tied",
      detail:
        lender.state === "tied"
          ? "What the HUD charges for the lender matches what was withheld from the wire."
          : lender.state === "off"
            ? `Off by ${signedMoney(lender.variance)}.`
            : lender.noHud
              ? "No HUD to compare."
              : "No HUD line names the lender, so the fees cannot be tied out from this statement.",
    },
    {
      key: "amount",
      label: "Ticket amount matches the calculator",
      ok: agreement.state === "agree",
      detail:
        agreement.state === "agree"
          ? "Statement, calculator and ticket all carry the same figure."
          : agreement.state === "mismatch"
            ? `The three net-wire figures spread by ${money(agreement.spread)}.`
            : "Enter the wire amount to check it against the calculator.",
    },
  ];
};

const CheckRow = ({ check }) => (
  <Flx gap={1.25} sx={{ alignItems: "flex-start", py: 0.85 }}>
    {check.ok ? (
      <CheckCircleRounded
        sx={{ fontSize: 17, color: T.green, flex: "none", mt: "1px" }}
      />
    ) : (
      <ReportProblemOutlined
        sx={{ fontSize: 17, color: T.amber, flex: "none", mt: "1px" }}
      />
    )}
    <Flx column gap={0.15} sx={{ minWidth: 0 }}>
      <Txt
        sx={{
          fontSize: 12.5,
          fontWeight: 600,
          color: check.ok ? T.ink : T.ink2,
        }}
      >
        {check.label}
      </Txt>
      <Txt sx={{ fontSize: 11.5, color: T.muted, lineHeight: 1.45 }}>
        {check.detail}
      </Txt>
    </Flx>
  </Flx>
);

const WireTicketPane = ({ statement, netWire, wireInstructions }) => {
  const checks = releaseChecks({ statement, netWire, wireInstructions });
  const blocking = checks.filter((c) => !c.ok);
  const amountCheck = checks.find((c) => c.key === "amount");
  const ticket = ticketProgress(wireInstructions);
  const released = ticket.progress === "done";

  const header = (
    <PaneHeader
      title="Wire Ticket"
      badges={<Pill label={ticket.status} tone={ticket.tone} />}
      subtitle="The operational status of the outgoing wire — requested and released dates, amount, fed reference. Edits are held until you save; nothing is written to loanTerms.wireInstructions before then."
    />
  );

  return (
    <PaneShell
      header={header}
      strip={
        <PaneStrip>
          <StripCell
            label="Calculator net wire"
            value={money(netWire?.netWireToTitle)}
            sub={
              Number(netWire?.netWireToTitle) < 0
                ? "incoming from title"
                : "the authoritative figure"
            }
          />
          <StripCell
            label="Statement net wire"
            value={money(statement?.netWireToTitle)}
            sub={statement?.statementStatus || "not generated"}
          />
          <StripCell
            label="Ticket amount"
            value={money(wireInstructions?.wireAmount)}
            sub={
              Number(wireInstructions?.wireAmount) < 0
                ? "incoming from title"
                : "typed below"
            }
            tone={
              wireInstructions?.wireAmount == null
                ? T.faint
                : amountCheck.ok
                  ? T.green
                  : T.red
            }
          />
          <StripCell
            label="Released"
            value={
              formatDate(
                wireInstructions?.closingWireReleaseDate,
                EMPTY,
                "MMM d, yyyy",
              ) || EMPTY
            }
            sub={wireInstructions?.wireStatus || "no status set"}
          />
        </PaneStrip>
      }
    >
      <Flx column gap={3} fw>
        <Callout
          tone={blocking.length === 0 ? "success" : released ? "error" : "warning"}
          icon={
            blocking.length === 0 ? (
              <CheckCircleRounded sx={{ fontSize: 20 }} />
            ) : (
              <ReportProblemOutlined sx={{ fontSize: 20 }} />
            )
          }
          title={
            blocking.length === 0
              ? "Clear to release"
              : released
                ? `Released with ${blocking.length} unmet ${blocking.length === 1 ? "check" : "checks"}`
                : `${blocking.length} ${blocking.length === 1 ? "check" : "checks"} outstanding`
          }
          body={
            blocking.length === 0
              ? "The statement is locked, the HUD ties out and the ticket amount agrees with the calculator."
              : "Prepping the ticket is fine — resolve these before the wire actually goes out."
          }
        />

        <Box>
          <SectionHead label="Release checks" />
          <Box sx={{ pt: 0.5 }}>
            {checks.map((c) => (
              <CheckRow key={c.key} check={c} />
            ))}
          </Box>
        </Box>

        <Box>
          <SectionHead label="Ticket" />
          <Box sx={{ pt: 2.5 }}>
            <LoanFeesSubTabWireInstructionsRedesign />
          </Box>
        </Box>
      </Flx>
    </PaneShell>
  );
};

WireTicketPane.propTypes = {
  statement: PropTypes.object,
  netWire: PropTypes.object,
  wireInstructions: PropTypes.object,
};

export default WireTicketPane;
